import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Grid, List, Typography } from '@material-ui/core';
import { darken, makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';

import { Network } from 'api';
import { useCapabilities, useSocialConnections, useNetworkCapabilities } from 'hooks';
import { connectionPopup, hasCapability } from 'helpers';
import { Connection, CustomSVG } from 'components';

const useStyles = makeStyles(theme => ({
  button: {
    borderRadius: 4,
    color: theme.palette.common.white,
    '&:hover': {
      color: theme.palette.common.white,
    },
  },
  twitter: {
    backgroundColor: theme.custom.networks.twitter,
    '&:hover': {
      backgroundColor: darken(theme.custom.networks.twitter, 0.1),
    },
  },
  linkedin: {
    backgroundColor: theme.custom.networks.linkedin,
    '&:hover': {
      backgroundColor: darken(theme.custom.networks.linkedin, 0.1),
    },
  },
  list: {
    paddingBottom: theme.spacing(1),
  },
}));

const Connections: FC = () => {
  const { t } = useTranslation();
  const { hasTwitter } = useNetworkCapabilities();

  const classes = useStyles();
  const capabilities = useCapabilities();
  const { connections, twitterAccounts, linkedinAccounts } = useSocialConnections();

  const showTwitterConnect =
    !twitterAccounts.length ||
    hasCapability(capabilities, 'Sharing.Function.ConnectMultipleAccounts');
  const showLinkedInConnect =
    !linkedinAccounts.length ||
    hasCapability(capabilities, 'Sharing.Function.ConnectMultipleAccounts');

  function renderConnections() {
    return connections.map(account => <Connection key={account.id} account={account} />);
  }

  return (
    <Fragment>
      <Grid container>
        <Grid item xs={3}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item>
              <CustomSVG color="action" name="activity" />
            </Grid>
            <Grid item>
              <Typography variant="subtitle2">{t('user:connectedAccounts')}</Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs>
          <List className={classes.list}>{renderConnections()}</List>
          <Grid container spacing={2}>
            {hasTwitter && showTwitterConnect && (
              <Grid item>
                <Button
                  variant="contained"
                  className={classNames(classes.button, classes.twitter)}
                  onClick={() => connectionPopup(Network.Twitter)}
                  startIcon={<CustomSVG viewBox="0 0 21 20" name="twitterOutline" />}
                >
                  {t('user:connectToTwitter')}
                </Button>
              </Grid>
            )}
            {showLinkedInConnect && (
              <Grid item>
                <Button
                  variant="contained"
                  className={classNames(classes.button, classes.linkedin)}
                  onClick={() => connectionPopup(Network.Linkedin)}
                  startIcon={<CustomSVG name="linkedinOutline" />}
                >
                  {t('user:connectToLinkedIn')}
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default Connections;
